import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_API_KEY, GET_API_KEYS, REVOKE_API_KEY } from '../../graphql';
import { useCopy } from '../../lib/copyToClipboard';
import { lastActiveLabel } from './accountAgents';
import { ConnDialog, CxInput } from './ConnDialog';
import { APPROVALS_PATH } from './goesOut';
import { AI_AGENTS } from '../cms/AskYourAgent';
import { askInk } from '../cms/InkConfirm';

/**
 * API & sources → API keys (Prototype v15): "New key" opens a dialog — who it's
 * for, what it can do, when it stops working — then shows the key once, to
 * copy; only its prefix is kept (apiserver platform/services/apiKeys.js).
 * Rows: name and prefix, when it stops, scope, last use, and Revoke. Admins
 * make and revoke; the server refuses anyone else.
 */

interface ApiKey {
  id: string;
  label: string;
  prefix: string;
  scope: 'read' | 'readwrite' | string;
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
}

type Scope = 'read' | 'readwrite';
type Expiry = '90' | '365' | 'never';

const SCOPE_WORDS: Record<string, string> = { read: 'Read only', readwrite: 'Read & write' };
const SCOPE_HELP: Record<Scope, string> = {
  read: 'Asks your archive (Research). Can’t change anything.',
  readwrite:
    'Also sends articles in and creates social posts. Posts still wait for approval on “Ask me first” channels.',
};
const EXPIRY_WORDS: Record<Expiry, string> = { '90': '90 days', '365': '1 year', never: 'Never' };

const stops = (iso?: string | null) =>
  iso
    ? `Stops ${new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : 'Never expires';

function NewKeyDialog({
  accountId,
  onClose,
  onMade,
}: {
  accountId: string;
  onClose: () => void;
  onMade: () => void;
}) {
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState<Scope>('read');
  const [exp, setExp] = useState<Expiry>('90');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const [shown, setShown] = useState<{ label: string; token: string } | null>(null);
  const [create, { loading }] = useMutation(CREATE_API_KEY);
  const { copy, state } = useCopy();

  if (shown) {
    const copied = state('key');
    return (
      <ConnDialog
        title={`Key for ${shown.label} is ready`}
        sub={`It’s in your list now as ${shown.label}.`}
        onClose={onClose}
        cta={{ label: 'Done, I’ve copied it', onClick: onClose }}
      >
        <div className="cx-key">
          <strong>Copy it now: it’s shown only once.</strong>
          <div className="cx-copy-row">
            <code className="cx-value">{shown.token}</code>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={(e) => void copy('key', shown.token, e)}
            >
              {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Couldn’t copy' : 'Copy'}
            </button>
          </div>
          <span className="cx-note">
            Send it privately, not in a shared doc. They add it as an{' '}
            <code>Authorization: Bearer</code> header. If it’s lost, revoke it and make a new one.
          </span>
        </div>
      </ConnDialog>
    );
  }

  const labelBad = tried && !label.trim();
  return (
    <ConnDialog
      title="New API key"
      sub="For a partner or a script that reads or adds content."
      onClose={onClose}
      cta={{
        label: loading ? 'Creating…' : 'Create key',
        disabled: loading,
        onClick: async () => {
          if (!label.trim()) return setTried(true);
          setError('');
          try {
            const { data: res } = await create({
              variables: {
                accountId,
                label: label.trim(),
                scope,
                expiresInDays: exp === 'never' ? null : Number(exp),
              },
            });
            const made = res?.createApiKey;
            if (made) setShown({ label: made.key.label, token: made.token });
            onMade();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'The key wasn’t made. Try again.');
          }
        },
      }}
    >
      <CxInput
        label="Who it’s for"
        value={label}
        onChange={setLabel}
        placeholder="e.g. Daily Sports Wire"
        autoFocus
        bad={labelBad}
        note={
          labelBad
            ? 'Say who it’s for, so you know what stops if you revoke it.'
            : 'Shown in the list and in Activity.'
        }
      />
      <div className="cx-field">
        <span className="cx-label">What it can do</span>
        <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
          {(['read', 'readwrite'] as const).map((s) => (
            <button
              key={s}
              type="button"
              data-active={scope === s ? 'true' : undefined}
              onClick={() => setScope(s)}
            >
              {SCOPE_WORDS[s]}
            </button>
          ))}
        </span>
        <span className="cx-note">{SCOPE_HELP[scope]}</span>
      </div>
      <div className="cx-field">
        <span className="cx-label">Stops working after</span>
        <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
          {(['90', '365', 'never'] as const).map((x) => (
            <button
              key={x}
              type="button"
              data-active={exp === x ? 'true' : undefined}
              onClick={() => setExp(x)}
            >
              {EXPIRY_WORDS[x]}
            </button>
          ))}
        </span>
        <span className="cx-note">
          {exp === 'never'
            ? 'It works until you revoke it.'
            : 'It stops on its own then; make a new one to keep going.'}
        </span>
      </div>
      {error ? (
        <p className="cx-note" data-bad="true">
          {error}
        </p>
      ) : null}
    </ConnDialog>
  );
}

export function ApiKeysSection({ accountId }: { accountId: string }) {
  const { data, refetch } = useQuery<{ apiKeys: ApiKey[] }>(GET_API_KEYS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [revoke] = useMutation(REVOKE_API_KEY);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const keys = data?.apiKeys || [];

  const drop = async (k: ApiKey) => {
    if (
      !(await askInk({
        title: `Revoke the key for ${k.label}?`,
        body: 'Anything using it stops working at once.',
        note: 'You can’t undo this.',
        confirmLabel: 'Revoke key',
        danger: true,
      }))
    )
      return;
    setError('');
    try {
      await revoke({ variables: { accountId, id: k.id } });
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The key wasn’t revoked. Try again.');
    }
  };

  return (
    <>
      {/* Account → API & sources › Third-party access (2026-10-24). These are the
          smk_ API keys; a read-only one reads through the API, not the feeds.
          Don't say a write key can't publish: its posts scope goes out on a
          channel set to Goes out without asking (backlog 7654d9ef). */}
      <div className="ink-conn-head" id="api-keys">
        <div>
          <h2>Writes to tinycms</h2>
          <p className="ink-acct-head-desc">
            For scripts and partner systems that send articles and social posts straight to tinycms,
            with no chat. Posts follow each channel’s setting in{' '}
            <Link to={APPROVALS_PATH}>Approvals</Link>. Each change is listed on the item it touched
            and in the Activity Log. To connect an AI assistant like Claude or ChatGPT, use{' '}
            <Link to={AI_AGENTS}>AI Agents</Link>. To only read content, make a key under Reads your
            content.
          </p>
        </div>
        <button type="button" className="ink-btn" onClick={() => setOpen(true)}>
          New key
        </button>
      </div>
      {error ? <p className="ink-lede">{error}</p> : null}
      <div className="ink-conn-keys">
        {keys.length === 0 ? (
          <div className="ink-bill-empty" style={{ padding: '15px 0' }}>
            No keys. Make one when a partner or script needs to send articles or social posts in.
          </div>
        ) : (
          keys.map((k) => (
            <div className="ink-key-row" key={k.id}>
              <div style={{ minWidth: 0 }}>
                <div className="ink-key-name">{k.label}</div>
                <div className="ink-key-prefix">
                  {k.prefix}•••• · {stops(k.expiresAt)}
                </div>
              </div>
              <span className="ink-muted">{SCOPE_WORDS[k.scope] || k.scope}</span>
              <span className="ink-muted">
                {k.lastUsedAt ? `Used ${lastActiveLabel(k.lastUsedAt)}` : 'Not used yet'}
              </span>
              <button
                type="button"
                className="ink-bill-link"
                style={{ color: 'var(--ink-red)' }}
                onClick={() => void drop(k)}
              >
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
      {open ? (
        <NewKeyDialog
          accountId={accountId}
          onClose={() => setOpen(false)}
          onMade={() => void refetch()}
        />
      ) : null}
    </>
  );
}
